import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGenerateIdeas } from "@/hooks/use-ideas";
import { useAgentJob } from "@/hooks/use-agent-jobs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lightbulb, Loader2, Sparkles } from "lucide-react";

/**
 * AI-action empty state for the Ideas board: instead of a blank kanban,
 * offer a topic input that kicks off ideation and polls the AgentJob
 * until the fresh ideas land.
 */
export function GenerateIdeasEmpty({ brandId }: { brandId: string }) {
  const { t } = useTranslation("ideas");
  const qc = useQueryClient();
  const [topic, setTopic] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);

  const generateMut = useGenerateIdeas(brandId);
  const { data: job } = useAgentJob(brandId, jobId);

  const settledJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (!job || !jobId || settledJobRef.current === jobId) return;
    if (job.status === "COMPLETED") {
      settledJobRef.current = jobId;
      qc.invalidateQueries({ queryKey: ["ideas", brandId] });
      toast.success(t("ideas:empty.toast.success"));
      setJobId(null);
    } else if (job.status === "FAILED") {
      settledJobRef.current = jobId;
      toast.error(job.error ?? t("ideas:empty.toast.failed"));
      setJobId(null);
    }
  }, [job, jobId, brandId, qc, t]);

  const generating = generateMut.isPending || !!jobId;

  function handleGenerate() {
    const trimmed = topic.trim();
    if (!trimmed || generating) return;
    generateMut.mutate(
      { topic: trimmed, count: 5 },
      {
        onSuccess: (data) => setJobId(data.jobId),
        onError: () => toast.error(t("ideas:empty.toast.startFailed")),
      },
    );
  }

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-16 text-center">
      <Lightbulb size={48} className="mb-4 text-muted-foreground/50" />
      <h3 className="text-lg font-semibold">{t("ideas:empty.title")}</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        {t("ideas:empty.description")}
      </p>
      <div className="mt-4 flex w-full max-w-md gap-2">
        <Input
          placeholder={t("ideas:empty.placeholder")}
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
          disabled={generating}
          className="flex-1"
        />
        <Button onClick={handleGenerate} disabled={!topic.trim() || generating}>
          {generating ? (
            <>
              <Loader2 size={14} className="mr-2 animate-spin" /> {t("ideas:empty.generating")}
            </>
          ) : (
            <>
              <Sparkles size={14} className="mr-2" /> {t("ideas:empty.generate")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
